import { PaperClipOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Flex, Input, Tag, Upload } from "antd";
import type { UploadFile } from "antd/es/upload";
import { useState } from "react";
import { FileListItem } from "./FileListItem";

type ReplyTarget = { id: number; name: string };

export type UploadedCommentAttachment = {
  name?: string | null;
  size?: number | null;
  content_type?: string | null;
  file_path?: string | null;
};

export type CommentNewFiles = Record<
  string,
  { name: string; size: number; content_type: string; path: string }
>;

function buildNewFiles(
  attachments: UploadFile<UploadedCommentAttachment>[]
): CommentNewFiles {
  const newFiles: CommentNewFiles = {};
  for (const { response } of attachments) {
    if (response?.file_path) {
      newFiles[response.file_path] = {
        name: response.name ?? "",
        size: response.size ?? 0,
        content_type: response.content_type ?? "",
        path: response.file_path,
      };
    }
  }
  return newFiles;
}

type CommentComposerProps = {
  onSubmit: (content: string, replyTo?: number, newFiles?: CommentNewFiles) => Promise<void>;
  onUploadAttachment: (file: File) => Promise<UploadedCommentAttachment>;
  replyingTo: ReplyTarget | null;
  onClearReply: () => void;
  intl: IntlShape;
};

export const CommentComposer = ({
  onSubmit,
  onUploadAttachment,
  replyingTo,
  onClearReply,
  intl,
}: CommentComposerProps) => {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<UploadFile<UploadedCommentAttachment>[]>(
    []
  );

  const hasUploadingAttachment = attachments.some(
    (attachment) => attachment.status === "uploading"
  );
  const hasFailedAttachment = attachments.some(
    (attachment) => attachment.status === "error"
  );
  const canSubmit = !!content.trim() && !hasUploadingAttachment && !hasFailedAttachment;

  const handleSubmit = async () => {
    const trimmed = content.trim();
    if (!trimmed || !canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed, replyingTo?.id, buildNewFiles(attachments));
      setContent("");
      setAttachments([]);
    } finally {
      setSubmitting(false);
    }
  };

  const removeAttachment = (uid: string) =>
    setAttachments((current) => current.filter((attachment) => attachment.uid !== uid));

  return (
    <Flex vertical gap={8}>
      {replyingTo && (
        <Tag
          closable
          onClose={onClearReply}
          style={{ width: "fit-content" }}
        >
          Replying to {replyingTo.name}
        </Tag>
      )}
      <Input.TextArea
        rows={3}
        placeholder="Write a comment..."
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />
      {attachments.length > 0 && (
        <Flex vertical gap={4}>
          {attachments.map((attachment) => (
            <FileListItem
              key={attachment.uid}
              file={attachment}
              onRemove={() => removeAttachment(attachment.uid)}
              intl={intl}
            />
          ))}
        </Flex>
      )}
      <Flex justify="space-between" align="center" gap={8}>
        <Upload
          multiple
          showUploadList={false}
          fileList={attachments}
          onChange={({ fileList }) => setAttachments(fileList)}
          customRequest={({ file, onSuccess, onError }) => {
            if (!(file instanceof File)) return;
            onUploadAttachment(file)
              .then((uploadedAttachment) => onSuccess?.(uploadedAttachment))
              .catch((error: Error) => onError?.(error));
          }}
        >
          <Button icon={<PaperClipOutlined />}>
            {intl.formatMessage({ id: "comment_attach_files" })}
          </Button>
        </Upload>
        <Button
          type="primary"
          disabled={!canSubmit}
          loading={submitting}
          onClick={handleSubmit}
        >
          {replyingTo ? "Reply" : "Post Comment"}
        </Button>
      </Flex>
    </Flex>
  );
};
